import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { updateReferral, getDistricts } from "../../lib/api";
import { REFERRAL_OUTCOME_REASONS } from "../../lib/referralOutcomes";
import { EnrollmentWizard } from "./EnrollmentWizard";

const FIELDS = [
  "referral_status", "grade", "referred_from", "sending_district", "referral_best_contact",
  "referral_best_phone", "first_contact_date", "phone_screen_date", "tour_date", "class_visit_date",
  "interview_date", "referral_notes", "packet_url",
  "waitlist_date", "declined_date", "outcome_reason", "outcome_notes",
];

export function ReferralEditor({ referral, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    getDistricts().then((r) => {
      const list = (r.data || []).filter((d) => d?.name).sort((a, b) => a.name.localeCompare(b.name));
      setDistricts(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const f = {};
    FIELDS.forEach((k) => { f[k] = referral?.[k] || ""; });
    if (!f.referral_status) f.referral_status = "in_process";
    setForm(f);
  }, [referral?.id]); // eslint-disable-line

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Assigning a district resolves any "needs review" flag on this referral.
      if (form.sending_district) payload.sending_district_needs_review = false;
      const res = await updateReferral(referral.id, payload);
      toast.success("Referral updated. Tour / Class Visit / Interview dates synced to the staff calendar.");
      onSaved?.(res.data, false);
    } catch {
      toast.error("Failed to save referral");
    } finally {
      setSaving(false);
    }
  };

  const dateField = (key, label) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="date" value={form[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`referral-${key}`} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="referral-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Pipeline Status</Label>
          <Select value={form.referral_status || "in_process"} onValueChange={(v) => set("referral_status", v)}>
            <SelectTrigger data-testid="referral-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="in_process">In Process</SelectItem>
              <SelectItem value="waitlist">Waitlist</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Grade</Label>
          <Input value={form.grade || ""} onChange={(e) => set("grade", e.target.value)} data-testid="referral-grade" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Referred From</Label>
          <Input value={form.referred_from || ""} onChange={(e) => set("referred_from", e.target.value)} placeholder="Person who referred them" data-testid="referral-referred-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sending District</Label>
          <Select value={form.sending_district || undefined} onValueChange={(v) => set("sending_district", v)}>
            <SelectTrigger data-testid="referral-sending-district"><SelectValue placeholder="Select a sending district…" /></SelectTrigger>
            <SelectContent>
              {districts.map((d) => (
                <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Best Contact</Label>
          <Input value={form.referral_best_contact || ""} onChange={(e) => set("referral_best_contact", e.target.value)} data-testid="referral-contact" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Contact Phone</Label>
          <Input value={form.referral_best_phone || ""} onChange={(e) => set("referral_best_phone", e.target.value)} data-testid="referral-phone" />
        </div>
        {dateField("first_contact_date", "Date of First Contact")}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {dateField("phone_screen_date", "Phone Screen")}
        {dateField("tour_date", "Tour")}
        {dateField("class_visit_date", "Class Visit")}
        {dateField("interview_date", "Interview")}
      </div>
      <p className="text-xs text-muted-foreground -mt-1">Tour, Class Visit, and Interview dates are added to the staff calendar automatically.</p>

      {(form.referral_status === "waitlist" || form.referral_status === "declined") && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 space-y-3" data-testid="referral-outcome-block">
          <p className="text-xs font-semibold text-amber-800">Outcome — why this referral didn&apos;t enroll</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {form.referral_status === "waitlist" && dateField("waitlist_date", "Waitlist Date")}
            {form.referral_status === "declined" && dateField("declined_date", "Rejection Date")}
            <div className="space-y-1">
              <Label className="text-xs">Referral Outcome Reason</Label>
              <Select value={form.outcome_reason || undefined} onValueChange={(v) => set("outcome_reason", v)}>
                <SelectTrigger data-testid="referral-outcome-reason"><SelectValue placeholder="Select a reason…" /></SelectTrigger>
                <SelectContent>
                  {REFERRAL_OUTCOME_REASONS.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`referral-outcome-reason-${r}`}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Referral Outcome Notes</Label>
            <Textarea rows={2} value={form.outcome_notes || ""} onChange={(e) => set("outcome_notes", e.target.value)} placeholder="Additional context about why this referral didn't enroll" data-testid="referral-outcome-notes" />
          </div>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs">Referral Packet Link</Label>
        <div className="flex gap-2">
          <Input value={form.packet_url || ""} onChange={(e) => set("packet_url", e.target.value)} placeholder="https://drive.google.com/... (link to the referral packet)" data-testid="referral-packet-url" />
          {form.packet_url ? (
            <Button type="button" variant="outline" onClick={() => window.open(form.packet_url, "_blank", "noopener")} data-testid="referral-packet-open">Open</Button>
          ) : null}
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Notes</Label>
        <Textarea rows={3} value={form.referral_notes || ""} onChange={(e) => set("referral_notes", e.target.value)} data-testid="referral-notes" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" onClick={() => setWizardOpen(true)} data-testid="referral-accept-open-btn">
          Enroll (Step-by-Step)
        </Button>
        <Button onClick={save} disabled={saving} data-testid="referral-save-btn">
          {saving ? "Saving..." : "Save Referral"}
        </Button>
      </div>

      <EnrollmentWizard
        referral={referral}
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onEnrolled={(updated) => onSaved?.(updated, true)}
      />
    </div>
  );
}
